// Added by Sejune Oh on 2019-05-15
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef, RefObject } from 'react'
import { observer, inject } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'
import { RouteChildrenProps } from 'react-router'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
  SIGN_ERR_STATUS_CODE,
  InputBlurValidator,
  InputValidator,
  MgmtValidator,
  ReactEmptyEvent,
  AUTH_TOKEN_COOKIE,
  ACCESS_TOKEN,
} from '@/helper/define'

// Util
import { deleteCookie, getFormData } from '@/helper/util'

import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Components
import ResetUserPassword from './ResetUserPassword'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import { UserStore, UserElementProps } from '@/stores/mgmt/user'
import { UserAuthStore } from '@/stores/user/UserAuth'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import withUserValidator from '../UserValidator'
import EventStore from '@/stores/EventStore'

interface ResetUserPasswordCProps extends MgmtValidator, RouteChildrenProps {
  userAuth?: UserAuthStore
  user?: UserStore
  globalEvt?: EventStore
  emailRef: RefObject<any>
  usernameRef: RefObject<any>
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'user', 'globalEvt')
@observer
class ResetUserPasswordContainer extends Component<ResetUserPasswordCProps> {
  isErrorOccured: boolean = false
  errMessage: string = ''

  _formId = 'r-pw'
  _curPwdRef = createRef<any>()
  _newPwdRef = createRef<any>()
  _newConfirmPwdRef = createRef<any>()
  _dialogRef = createRef<any>()

  constructor(props: ResetUserPasswordCProps) {
    super(props)

    // user가 manual하게 url을 변경하여 이동하는 경우를 막기 위해 쿠키를 지운다.
    deleteCookie(AUTH_TOKEN_COOKIE)
    deleteCookie(ACCESS_TOKEN)

    makeObservable(this, {
      isErrorOccured: observable,
      errMessage: observable,
      _handleSnackbarClose: action,
      _handlePasswordUpdate: action,
      userAuth: computed,
    })
  }

  componentDidMount = () => {
    const title = this.userAuth.isExpiredPassword
      ? Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpasswordcontainer.title_pw_expired' })
      : Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpasswordcontainer.title_pw_reset' })

    const description = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpasswordcontainer.desc_pw_change' })
    this._dialogRef.current!.setDialogInfo({ title, description, hideCancelBtn: true }).handleOpen()
  }

  render() {
    const { blurValidator: _blurValdiator, validator: _validator, ...others } = this.props
    const validator = {
      ..._validator,
      pwdValidator: this._pwdValidator,
      confirmPwdValidator: this._confirmPwdValidator,
    }
    const blurValidator = {
      ..._blurValdiator,
      pwdBlurValidator: this._pwdBlurValidator,
      confirmPwdBlurValidator: this._confirmPwdBlurValidator,
    }

    return (
      <ResetUserPassword
        {...others}
        formId={this._formId}
        validator={validator}
        blurValidator={blurValidator}
        userName={this.userAuth.userInfo!.username!}
        userEmail={this.userAuth.userInfo!.email!}
        curPwdRef={this._curPwdRef}
        newPwdRef={this._newPwdRef}
        newConfirmPwdRef={this._newConfirmPwdRef}
        onConfirmBtnClick={this._handlePasswordUpdate}
        onCancelBtnClick={this._handleCancelBtnClick}
        isErrorOccured={this.isErrorOccured}
        onSnackbarClose={this._handleSnackbarClose}
        errMessage={this.errMessage}
        dialogRef={this._dialogRef}
      />
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleSnackbarClose: ReactEmptyEvent = () => {
    this.isErrorOccured = false
  }

  _handlePasswordUpdate: ReactEmptyEvent = async () => {
    try {
      this.props.globalEvt!.setLoadingOn()

      const form = document.getElementById(this._formId) as HTMLFormElement
      const _curPwdRef = this._curPwdRef!.current!
      const changePwdInfo = this._getFormData(form)

      _curPwdRef.callValidator()

      if (!_curPwdRef.isErrorOccured) {
        await this.props.user!.updatePassword(changePwdInfo!)
        this.userAuth.clearPasswordInitialization()
      }

      // 쿠키가 삭제된 상태이므로 새로 로그인한다.
      const {authToken} = await this.userAuth.checkUserInfo({
        email: changePwdInfo?.username,
        password: changePwdInfo?.password,
      })
      await this.userAuth.signIn(authToken)
    } catch (err) {
      let errMessage = err.message

      if (err.response) {
        const { status, data } = err.response;
        errMessage = data.message;
        // 에러 메시지에 invalid current password 라는 문구가 포함되어 있으면 password가 잘못되었다는 메시지 출력
        if (errMessage?.includes('invalid current password')) {
          return this._curPwdRef.current!.setErrorState({
            validateStatus: true,
            errMessage: this.userAuth.getSignInErrMessage(Intl, 'wPwinfo')
          })
        }
        errMessage = this.userAuth.getSignInErrMessage(Intl, SIGN_ERR_STATUS_CODE[`${status}`])
      }

      this.errMessage = errMessage
      this.isErrorOccured = true
    } finally {
      this.props.globalEvt!.setLoadingOff()
    }
  }

  get userAuth() {
    return this.props.userAuth!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _getFormData = (form: HTMLFormElement) => {
    let formData = getFormData<UserElementProps>(form)
    formData!.id = this.userAuth.userInfo!.id
    return formData
  }

  _handleCancelBtnClick: ReactEmptyEvent = () => {
    this.userAuth.signOut(true)
  }

  _pwdValidator: InputValidator = (value) => {
    if (this._newConfirmPwdRef.current) this._newConfirmPwdRef.current!.callValidator()
    return this.props.validator.pwdValidator(value)
  }

  _confirmPwdValidator: InputValidator = (value) => {
    const curPwd = this._newPwdRef.current!.value

    if (curPwd !== value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpasswordcontainer.msg_pwd_not_matched' })
      return [true, eMessage]
    }

    return [false]
  }

  _pwdBlurValidator: InputBlurValidator = async (value) => {
    if (this._newConfirmPwdRef.current) this._newConfirmPwdRef.current!.callValidator()
    return this.props.blurValidator!.pwdBlurValidator(value)
  }

  _confirmPwdBlurValidator: InputBlurValidator = async (value) => {
    const curPwd = this._newPwdRef!.current!.value

    if (curPwd !== value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpasswordcontainer.msg_pwd_not_matched' })
      return [true, eMessage]
    }

    return [false]
  }
}

export default withUserValidator()(ResetUserPasswordContainer)
